import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import type { AlignmentValue } from '@elfsight/widget-ui/src/title/types';
import { createReadableClass } from '@elfsight/widget-utils';
import { useRect } from 'react-use-rect';
import type { LocationLabel } from '../../common';
import { useLocationCardProps } from '../location-card-settings-context';
import { LocationLabelsItem } from './location-labels-item';

type LocationLabelsProps = {
  labels: LocationLabel[];
  alignment: AlignmentValue;
};

export const LocationLabels = ({ labels, alignment }: LocationLabelsProps) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const [lastElementsInRows, setLastElementsInRows] = useState<Set<number>>(
    new Set()
  );
  const labelElementRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { textColor } = useLocationCardProps();

  const [containerRef] = useRect(({ width }) => setContainerWidth(width), {
    resize: true
  });

  useEffect(() => {
    const rowsByTopPosition: Record<number, number[]> = {};

    labelElementRefs.current.forEach((item, index) => {
      if (!item) {
        return;
      }

      const topPosition = item.offsetTop;

      if (!rowsByTopPosition[topPosition]) {
        rowsByTopPosition[topPosition] = [];
      }

      rowsByTopPosition[topPosition].push(index);
    });

    const lastElementsSet = new Set<number>();

    Object.values(rowsByTopPosition).forEach((rowByTopPosition) => {
      const lastIndex = rowByTopPosition[rowByTopPosition.length - 1];
      lastElementsSet.add(lastIndex);
    });

    setLastElementsInRows(lastElementsSet);
  }, [labels, containerWidth]);

  return (
    <LabelsContainer ref={containerRef} _alignment={alignment}>
      {labels.map(({ id, text }, index) => {
        const isDotVisible =
          !lastElementsInRows.has(index) && index !== labels.length - 1;

        return (
          <LabelsItemContainer
            key={id}
            ref={(element) => (labelElementRefs.current[index] = element)}
            _color={textColor}
            _isDotVisible={isDotVisible}
          >
            <LocationLabelsItem text={text} />
            {isDotVisible && <Dot _color={textColor} />}
          </LabelsItemContainer>
        );
      })}
    </LabelsContainer>
  );
};

const LabelsContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'location-labels',
    element: 'labels-container'
  })
})<{ _alignment: AlignmentValue }>`
  display: flex;
  gap: 4px 0;
  flex-wrap: wrap;
  justify-content: ${({ _alignment }) => _alignment || 'left'};
  line-height: 1.23;
  font-size: 13px;
  font-weight: 700;
`;

const LabelsItemContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'location-labels',
    element: 'labels-item-container'
  })
})<{ _color: string; _isDotVisible: boolean }>`
  display: flex;
  align-items: center;
  margin-right: 8px;
  color: ${({ _color }) => getColorWithAlpha(_color, 0.5)};

  ${({ _isDotVisible }) =>
    !_isDotVisible &&
    css`
      padding-right: 12px;
    `}
`;

const Dot = styled.div.attrs({
  className: createReadableClass({
    component: 'location-labels',
    element: 'dot'
  })
})<{ _color: string }>`
  display: inline-block;
  height: 4px;
  width: 4px;
  margin-left: 8px;
  border-radius: 50%;
  background-color: ${({ _color }) => getColorWithAlpha(_color, 0.5)};
`;
